import React from 'react';
import { observer } from 'mobx-react';
import { observable } from 'mobx';
import ErrorBoundary from 'components/hoc/error_boundary.js';
import styled from '@emotion/styled';
import InPlaceText from 'components/form/in_place_text';
import { RecordingSessionStore , VideoClipStore, UserStore, RouteStore } from '@seedlang/stores';
import { Link } from 'react-router';
import VideoClipIndex from 'pages/builder/video_clips/video_clip_index';
import autobind from 'autobind-decorator';
import Alert from 'components/alert';
import InPlaceSelect from 'components/form/in_place_select';
import DeleteButton from 'components/button/delete_button';
import InPlaceCheckbox from 'components/form/in_place_checkbox';
import { isPresent } from '@seedlang/utils';
import Stack from 'components/layout/stack';
import Grid from 'components/layout/grid';
import GrayButton from 'components/button/gray_button';

const SmallText = styled.div`
  font-size: 12px;
  line-height: 14px;
  margin: ${props => props.margin};
  color: #333;
`;

const ReviewedUser = styled.div`
  font-size: 12px;
  padding-left: 18px;
  background: #FFF;
  margin: 0 0 5px 0;
`;

@observer
class RecordingSessionsShow extends React.Component {
  @observable showMessage = false;

  constructor(props) {
    super(props);
    this.loadRecordingSession();
    UserStore.getIndex({filters: {video_creator: true}});
  }

  @autobind onClickClear(queryStrings) {
    RecordingSessionStore.removeVideosFromTargets({queryStrings: queryStrings, ids: {recordingSessionId: this.props.params.recordingSessionId}}, this.afterClickClear);
  }

  @autobind onDestroyRecordingSession() {
    RecordingSessionStore.destroy({ids: {recordingSessionId: this.props.params.recordingSessionId}}, this.afterDestroySession);
  }

  @autobind afterDestroySession() {
    RouteStore.routeToNamed('creator.producer.recording_sessions.index');
  }

  @autobind loadRecordingSession() {
    RecordingSessionStore.getShow({ids: {recordingSessionId: this.props.params.recordingSessionId}}, this.afterGetShow);
  }

  @autobind afterGetShow(resp) {
    RecordingSessionStore.setShowData(resp);
  }

  @autobind afterClickClear() {
    this.showMessage = true;
    this.loadRecordingSession();
    VideoClipStore.reloadIndexFiltered();
  }

  render() {
    return (
      <Stack gap='sm' alignItems='stretch'>
        {
          this.showMessage &&
            <Alert
              textAlign='center'
            >
              Videos have been cleared.
            </Alert>
        }
        <div
          style={{margin: '10px 0', textDecoration: 'underline'}}
        >
          <Link
            to='creator.producer.recording_sessions.index'
          >
            <i className='fa fa-angle-left' style={{margin: '2px 3px 0 0'}} /> Back to Recording Sessions
          </Link>
        </div>
        <Grid columns={[1,1,2]}>
          <fieldset>
            <legend>Date</legend>
            {RecordingSessionStore.hasShowData && RecordingSessionStore.showData.occurredAt.formattedDateWithYear}
          </fieldset>
          <fieldset>
            <legend>User</legend>
              <InPlaceSelect
                includeBlank
                model='recording_sessions'
                id={RecordingSessionStore.showData.id}
                field='user_id'
                options={UserStore.indexData.map(item => [item.id, item.name])}
                afterChange={this.loadRecordingSession}
                value={RecordingSessionStore.showData?.user?.id}
              />
          </fieldset>
          <fieldset>
            <legend>Clear Videos</legend>
            <div style={{display: 'flex'}}>
              <DeleteButton
                onConfirm={() => this.onDestroyRecordingSession()}
                message='Clear all videos? This is not reversible.'
              >
                <GrayButton style={{marginRight: '5px'}}>
                  All
                </GrayButton>
              </DeleteButton>
              <DeleteButton
                onConfirm={() => this.onClickClear({only_not_reviewed: true})}
                message='Clear all not approved videos? This is not reversible.'
              >
                <GrayButton style={{marginRight: '5px'}}>
                  Not Approved
                </GrayButton>
              </DeleteButton>
              <DeleteButton
                onConfirm={() => this.onClickClear({only_words: true})}
                message='Clear word videos? This is not reversible.'
              >
                <GrayButton style={{marginRight: '5px'}}>
                  Words
                </GrayButton>
              </DeleteButton>
              <DeleteButton
                onConfirm={() => this.onClickClear({only_nouns: true})}
                message='Clear noun videos? This is not reversible.'
              >
                <GrayButton>Nouns</GrayButton>
              </DeleteButton>
            </div>
          </fieldset>
        </Grid>
        <Grid columns={[1,3]}>
          <fieldset>
            <legend>Reviews</legend>
              <div>
              <InPlaceCheckbox
                model='recording_sessions'
                field='initial_reviewed'
                value={RecordingSessionStore.showData.initialReviewed}
                id={RecordingSessionStore.showData.id}
                icon='thumbs-up'
                afterChange={this.loadRecordingSession}
              >
                Initial Review
              </InPlaceCheckbox>
            </div>
            <SmallText
              margin='5px 0'
            >
              Setup of lighting, sound, and framing was approved
            </SmallText>
            {
              isPresent(RecordingSessionStore.showData.initialReviewedUser) &&
                <ReviewedUser>
                  {RecordingSessionStore.showData.initialReviewedUser.name}
                </ReviewedUser>
            }
              <div>
              <InPlaceCheckbox
                model='recording_sessions'
                field='final_reviewed'
                value={RecordingSessionStore.showData.finalReviewed}
                id={RecordingSessionStore.showData.id}
                icon='thumbs-up'
                afterChange={this.loadRecordingSession}
              >
                Final Review
              </InPlaceCheckbox>
            </div>
            <SmallText
              margin='5px 0'
            >
              All videos were reviewed but not necessarily deleted
            </SmallText>
            {
              isPresent(RecordingSessionStore.showData.finalReviewedUser) &&
                <ReviewedUser>
                  {RecordingSessionStore.showData.finalReviewedUser.name}
                </ReviewedUser>
            }
          </fieldset>
          <fieldset>
              <legend>Notes</legend>
                <InPlaceText
                  id={RecordingSessionStore.showData.id}
                  field='description'
                  richText
                  defaultValue={RecordingSessionStore.showData.description}
                  model='recording_sessions'
                />
          </fieldset>
        </Grid>
        <fieldset>
          <legend>Video Clips</legend>
          <VideoClipIndex
            userId={RecordingSessionStore.showData?.user?.id}
            recordingSessionId={this.props.params.recordingSessionId}
          />
        </fieldset>
      </Stack>
    );
  }
}

export default ErrorBoundary(RecordingSessionsShow);
